import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../shared/components/ui/dialog";
import { Input } from "../../../shared/components/ui/input";

interface ContentProviderResult {
  id: string;
  slug: string;
  displayName: string;
  logoUrl: string | null;
  logoSqUrl: string | null;
  verified: boolean;
}

interface AddProviderDialogProps {
  readonly isOpen: boolean;
  readonly collectionKey: string;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

/**
 * ProviderSearchResultCard - Individual provider search result
 */
interface ProviderResultCardProps {
  readonly provider: ContentProviderResult;
  readonly adding: boolean;
  readonly onAdd: (providerId: string) => Promise<void>;
}

function ProviderSearchResultCard({ provider, adding, onAdd }: ProviderResultCardProps) {
  const handleAdd = () => {
    void onAdd(provider.id);
  };

  const logoSrc = provider.logoSqUrl || provider.logoUrl;

  return (
    <div className="flex items-center gap-4 py-3 px-2 hover:bg-neutral-800/50 rounded-lg transition-colors">
      <div className="w-12 h-12 rounded-lg bg-neutral-800 flex items-center justify-center overflow-hidden flex-shrink-0">
        {logoSrc ? (
          <img src={logoSrc} alt={provider.displayName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-neutral-500 text-lg font-medium">{provider.displayName.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-white font-medium truncate">{provider.displayName}</h3>
          {provider.verified && (
            <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Verified</span>
          )}
        </div>
        <p className="text-sm text-neutral-500">@{provider.slug}</p>
      </div>
      <Button onClick={handleAdd} disabled={adding} variant="outline" size="sm">
        {adding ? "Adding..." : "Add"}
      </Button>
    </div>
  );
}

/**
 * SearchBar Component
 */
interface SearchBarProps {
  readonly query: string;
  readonly searching: boolean;
  readonly onQueryChange: (q: string) => void;
}

function SearchBar({ query, searching, onQueryChange }: SearchBarProps) {
  return (
    <div className="relative">
      <Input
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search providers by name..."
        className="w-full"
      />
      {searching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lumi-accent" />
        </div>
      )}
    </div>
  );
}

/**
 * SearchResults Component
 */
interface SearchResultsProps {
  readonly results: ContentProviderResult[];
  readonly searching: boolean;
  readonly adding: string | null;
  readonly onAdd: (providerId: string) => Promise<void>;
}

function SearchResults({ results, searching, adding, onAdd }: SearchResultsProps) {
  if (searching && results.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-400">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-lumi-accent" />
        <p className="mt-4">Searching providers...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-sm text-center py-12 text-neutral-600">No providers found. Try a different search term.</div>
    );
  }

  return (
    <div className="space-y-1">
      {results.map((provider) => (
        <ProviderSearchResultCard key={provider.id} provider={provider} adding={adding === provider.id} onAdd={onAdd} />
      ))}
    </div>
  );
}

/**
 * AddProviderDialog - Search for and add providers to a PROVIDER collection
 */
export function AddProviderDialog({ isOpen, collectionKey, onClose, onSuccess }: AddProviderDialogProps) {
  const { accessToken, refreshAuth } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ContentProviderResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Search providers when query changes (debounced)
  const searchProviders = useCallback(
    async (searchQuery: string) => {
      if (!accessToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (searchQuery.trim()) {
          params.set("search", searchQuery.trim());
        }
        params.set("limit", "20");

        const response = await authenticatedFetch<{ providers: ContentProviderResult[] }>(
          `${API_BASE}/v1/admin/content-providers?${params.toString()}`,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setResults(response.providers);
      } catch (err) {
        console.error("❌ Failed to search providers:", err);
        setError("Failed to search providers");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshAuth, API_BASE],
  );

  // Initial load and debounced search
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      void searchProviders(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, isOpen, searchProviders]);

  const handleAddProvider = async (providerId: string) => {
    if (!accessToken) return;

    setAdding(providerId);
    setError(null);

    try {
      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collectionKey}/items`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ providerId }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      onSuccess();
      handleClose();
    } catch (err) {
      console.error("❌ Failed to add provider:", err);
      setError("Failed to add provider to collection");
      setAdding(null);
    }
  };

  const handleClose = () => {
    setQuery("");
    setResults([]);
    setError(null);
    setAdding(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-black/50 border border-neutral-800 backdrop-blur-lg drop-shadow-md flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Provider to Collection</DialogTitle>
          <DialogDescription className="text-sm text-neutral-400">
            Search for content providers to add to this provider showcase
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-1 flex-1 overflow-hidden flex flex-col">
          <SearchBar query={query} searching={loading} onQueryChange={setQuery} />

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto pr-2">
            <SearchResults results={results} searching={loading} adding={adding} onAdd={handleAddProvider} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
